/**
 * AI Strategy Manager
 *
 * Central manager for AI strategies that handles:
 * - Strategy selection based on difficulty
 * - AI move generation
 * - Performance monitoring
 * - Strategy switching and management
 */

import { AIDifficulty, PlayerColor } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import type { IAIStrategy, AIMoveResult, AIPerformanceMetrics } from './ai-strategy-base';

// Import AI strategy implementations
import { createRandomAI } from './strategies/random-ai';
import { createGreedyAI } from './strategies/greedy-ai';
import { createMinimaxAI } from './strategies/minimax-ai';
import { createAdvancedAI } from './strategies/advanced-ai';

/**
 * AI strategy registry entry
 */
interface StrategyRegistryEntry {
  difficulty: AIDifficulty;
  name: string;
  description: string;
  factory: () => IAIStrategy;
  isDefault: boolean;
}

/**
 * AI Manager configuration options
 */
export interface AIManagerOptions {
  defaultDifficulty?: AIDifficulty;
  enablePerformanceLogging?: boolean;
  maxConcurrentThinking?: number;
  fallbackOnTimeout?: boolean;
}

/**
 * AI thinking status
 */
export interface AIThinkingStatus {
  isThinking: boolean;
  difficulty: AIDifficulty;
  strategyName: string;
  startTime: number;
  timeLimit: number;
  player: PlayerColor;
  gameState?: GameState;
}

/**
 * AI move request
 */
export interface AIMoveRequest {
  gameState: GameState;
  player: PlayerColor;
  difficulty: AIDifficulty;
  timeLimit?: number;
  priority?: number; // 0-10, higher = more priority
}

/**
 * AI Strategy Manager implementation
 */
export class AIStrategyManager {
  private _strategies = new Map<string, IAIStrategy>();
  private _strategyRegistry = new Map<AIDifficulty, StrategyRegistryEntry[]>();
  private _currentThinking: AIThinkingStatus | null = null;
  private _performanceHistory = new Map<AIDifficulty, AIPerformanceMetrics[]>();
  private _options: AIManagerOptions;
  private _worker: Worker | null = null;
  private _pendingWorkerRequests = new Map<string, { resolve: (result: AIMoveResult) => void; reject: (error: Error) => void }>();
  private _customSettings: any = null;

  constructor(options: AIManagerOptions = {}) {
    this._options = {
      defaultDifficulty: AIDifficulty.GREEDY,
      enablePerformanceLogging: true,
      maxConcurrentThinking: 1,
      fallbackOnTimeout: true,
      ...options,
    };

    this.registerDefaultStrategies();
  }

  /**
   * Register default AI strategies
   */
  private registerDefaultStrategies(): void {
    // Random AI variants
    this.registerStrategy({
      difficulty: AIDifficulty.RANDOM,
      name: 'Pure Random',
      description: 'Completely random move selection',
      factory: () => createRandomAI.pure(),
      isDefault: false,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.RANDOM,
      name: 'Smart Random',
      description: 'Random moves but avoids obviously bad positions',
      factory: () => createRandomAI.smart(),
      isDefault: true, // Default for RANDOM difficulty
    });

    // Greedy AI variants
    this.registerStrategy({
      difficulty: AIDifficulty.GREEDY,
      name: 'Standard Greedy',
      description: 'Selects highest immediate score',
      factory: () => createGreedyAI.standard(),
      isDefault: false,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.GREEDY,
      name: 'Enhanced Greedy',
      description: 'Greedy with additional heuristics',
      factory: () => createGreedyAI.enhanced(),
      isDefault: false,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.GREEDY,
      name: 'Adaptive Greedy',
      description: 'Greedy that adapts to game situation',
      factory: () => createGreedyAI.adaptive(),
      isDefault: true, // Default for GREEDY difficulty
    });

    // Minimax AI variants
    this.registerStrategy({
      difficulty: AIDifficulty.MINIMAX,
      name: 'Standard Minimax',
      description: 'Minimax with alpha-beta pruning (depth 6)',
      factory: () => createMinimaxAI.intermediate(),
      isDefault: true, // Default for MINIMAX difficulty
    });

    this.registerStrategy({
      difficulty: AIDifficulty.MINIMAX_EASY,
      name: 'Easy Minimax',
      description: 'Minimax with moderate depth (depth 4)',
      factory: () => createMinimaxAI.basic(),
      isDefault: true,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.MINIMAX_HARD,
      name: 'Hard Minimax',
      description: 'Minimax with deep search (depth 8)',
      factory: () => createMinimaxAI.advanced(),
      isDefault: true,
    });

    // Advanced AI variants
    this.registerStrategy({
      difficulty: AIDifficulty.ADVANCED,
      name: 'Tournament AI',
      description: 'Opening book + minimax + endgame solver',
      factory: () => createAdvancedAI.tournament(),
      isDefault: true, // Default for ADVANCED difficulty
    });

    this.registerStrategy({
      difficulty: AIDifficulty.MASTER,
      name: 'Master AI',
      description: 'Maximum strength with deep analysis',
      factory: () => createAdvancedAI.master(),
      isDefault: true,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.BLITZ,
      name: 'Blitz AI',
      description: 'Fast advanced AI for quick games',
      factory: () => createAdvancedAI.blitz(),
      isDefault: true,
    });

    // Custom variants (can be configured by user)
    this.registerStrategy({
      difficulty: AIDifficulty.CUSTOM,
      name: 'Custom Minimax',
      description: 'User-configurable minimax AI',
      factory: () => createMinimaxAI.custom({}),
      isDefault: false,
    });

    this.registerStrategy({
      difficulty: AIDifficulty.CUSTOM,
      name: 'Custom Advanced',
      description: 'User-configurable advanced AI',
      factory: () => createAdvancedAI.custom({}),
      isDefault: true,
    });
  }

  /**
   * Register a new AI strategy
   */
  registerStrategy(entry: StrategyRegistryEntry): void {
    if (!this._strategyRegistry.has(entry.difficulty)) {
      this._strategyRegistry.set(entry.difficulty, []);
    }

    const strategies = this._strategyRegistry.get(entry.difficulty)!;

    // If this is marked as default, unmark others
    if (entry.isDefault) {
      strategies.forEach(s => s.isDefault = false);
    }

    strategies.push(entry);
  }

  /**
   * Get or create strategy instance
   */
  private getStrategy(difficulty: AIDifficulty, strategyName?: string): IAIStrategy {
    const key = `${difficulty}_${strategyName || 'default'}`;

    if (!this._strategies.has(key)) {
      const strategies = this._strategyRegistry.get(difficulty);

      if (!strategies || strategies.length === 0) {
        throw new Error(`No strategies registered for difficulty: ${difficulty}`);
      }

      let strategy: StrategyRegistryEntry;

      if (strategyName) {
        const found = strategies.find(s => s.name === strategyName);
        if (!found) {
          throw new Error(`Strategy '${strategyName}' not found for difficulty: ${difficulty}`);
        }
        strategy = found;
      } else {
        // Find default strategy
        const defaultStrategy = strategies.find(s => s.isDefault);
        if (!defaultStrategy) {
          throw new Error(`No default strategy found for difficulty: ${difficulty}`);
        }
        strategy = defaultStrategy;
      }

      this._strategies.set(key, strategy.factory());
    }

    return this._strategies.get(key)!;
  }

  /**
   * Request AI move
   */
  async requestMove(request: AIMoveRequest): Promise<AIMoveResult> {
    // Check if already thinking
    if (this._currentThinking && this._options.maxConcurrentThinking! <= 1) {
      throw new Error('AI is already thinking. Wait for current move or stop thinking first.');
    }

    const timeLimit = request.timeLimit || 1000;

    // Set thinking status
    this._currentThinking = {
      isThinking: true,
      difficulty: request.difficulty,
      strategyName: this.getStrategyName(request.difficulty),
      startTime: Date.now(),
      timeLimit,
      player: request.player,
      gameState: request.gameState,
    };

    try {
      // For strong AI, use Web Worker to prevent main thread blocking
      if (this.shouldUseWorker(request.difficulty)) {
        return await this.requestMoveViaWorker(request);
      }

      // For simple AI, run on main thread (fast enough)
      return await this.requestMoveOnMainThread(request);

    } catch (error) {
      // Handle timeout or other errors
      if (this._options.fallbackOnTimeout && error instanceof Error) {
        return this.getFallbackMove(request);
      }
      throw error;
    } finally {
      this._currentThinking = null;
    }
  }

  /**
   * Get fallback move when primary strategy fails
   */
  private async getFallbackMove(request: AIMoveRequest): Promise<AIMoveResult> {
    // Use simple random fallback
    const fallbackStrategy = createRandomAI.pure();

    return await fallbackStrategy.selectMove(
      request.gameState,
      request.player,
      100, // Very quick fallback
    );
  }

  /**
   * Update custom AI configuration
   */
  updateCustomAIConfig(customSettings: any): void {
    // Store custom settings for worker use
    this._customSettings = customSettings;

    // Update custom minimax strategy
    this.registerStrategy({
      difficulty: AIDifficulty.CUSTOM,
      name: 'Custom Minimax',
      description: 'User-configurable minimax AI',
      factory: () => createMinimaxAI.custom({
        maxDepth: customSettings.searchDepth,
        maxTime: customSettings.thinkingTime * 1000,
        useIterativeDeepening: customSettings.iterativeDeepening,
        useTranspositionTable: customSettings.useTranspositionTable,
      }),
      isDefault: false,
    });

    // Update custom advanced strategy
    this.registerStrategy({
      difficulty: AIDifficulty.CUSTOM,
      name: 'Custom Advanced',
      description: 'User-configurable advanced AI',
      factory: () => createAdvancedAI.custom({
        minimaxDepth: customSettings.searchDepth,
        minimaxTimeLimit: customSettings.thinkingTime * 1000,
        thinkingTimeBase: customSettings.thinkingTime * 1000,
        thinkingTimeMax: customSettings.thinkingTime * 2000,
        useOpeningBook: customSettings.useOpeningBook,
        useEndgameSolver: customSettings.useEndgameSolver,
        adaptiveTimeManagement: customSettings.adaptiveTime,
        useIterativeDeepening: customSettings.iterativeDeepening,
      }),
      isDefault: true,
    });

    // Clear cached strategies to force recreation with new config
    this._strategies.delete(`${AIDifficulty.CUSTOM}_Custom Minimax`);
    this._strategies.delete(`${AIDifficulty.CUSTOM}_Custom Advanced`);
  }

  /**
   * Determine if AI should use Web Worker
   */
  private shouldUseWorker(difficulty: AIDifficulty): boolean {
    // Use worker for AI with search depth >= 4 to prevent main thread blocking
    return [
      AIDifficulty.MINIMAX_EASY,    // depth 4
      AIDifficulty.MINIMAX,         // depth 6
      AIDifficulty.MINIMAX_HARD,    // depth 8
      AIDifficulty.ADVANCED,        // depth 8+
      AIDifficulty.MASTER,          // depth 10+
      AIDifficulty.BLITZ,           // depth 6+
      AIDifficulty.CUSTOM,          // variable depth
    ].includes(difficulty);
  }

  /**
   * Get strategy name for difficulty
   */
  private getStrategyName(difficulty: AIDifficulty): string {
    const strategies = this._strategyRegistry.get(difficulty);
    if (strategies && strategies.length > 0) {
      const defaultStrategy = strategies.find(s => s.isDefault) || strategies[0];
      return defaultStrategy!.name;
    }
    return 'Unknown Strategy';
  }

  /**
   * Request move via Web Worker (for strong AI)
   */
  private async requestMoveViaWorker(request: AIMoveRequest): Promise<AIMoveResult> {
    return new Promise((resolve, reject) => {
      let timeoutId: number | null = null;
      let hasResolved = false;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const safeResolve = (result: AIMoveResult) => {
        if (!hasResolved) {
          hasResolved = true;
          cleanup();
          resolve(result);
        }
      };

      const safeReject = (error: Error) => {
        if (!hasResolved) {
          hasResolved = true;
          cleanup();
          reject(error);
        }
      };

      // Create worker if not exists
      if (!this._worker) {
        try {
          this._worker = new Worker(new URL('../../workers/ai-worker.ts', import.meta.url), {
            type: 'module',
          });

          this._worker.addEventListener('message', (event) => {
            this.handleWorkerMessage(event, safeResolve, safeReject);
          });

          this._worker.addEventListener('error', (error) => {
            console.error('AI Worker error:', error);
            console.log('Falling back to chunked processing on main thread');
            // Fallback to chunked processing
            this.requestMoveWithChunking(request).then(safeResolve).catch(safeReject);
          });
        } catch (error) {
          console.error('Failed to create AI Worker:', error);
          console.log('Falling back to chunked processing on main thread');
          // Fallback to chunked processing
          this.requestMoveWithChunking(request).then(safeResolve).catch(safeReject);
          return;
        }
      }

      // Generate unique request ID
      const requestId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
      this._pendingWorkerRequests.set(requestId, { resolve: safeResolve, reject: safeReject });

      try {
        // Send request to worker
        this._worker.postMessage({
          type: 'CALCULATE_MOVE',
          payload: {
            id: requestId,
            gameState: request.gameState,
            player: request.player,
            difficulty: request.difficulty,
            timeLimit: request.timeLimit || 1000,
            customConfig: this.getCustomConfig(request.difficulty),
          },
        });

        // Set timeout for worker request with improved handling
        const maxTimeout = Math.max((request.timeLimit || 1000) * 2, 5000); // At least 5 seconds
        timeoutId = window.setTimeout(() => {
          if (this._pendingWorkerRequests.has(requestId)) {
            console.warn(`AI Worker timeout after ${maxTimeout}ms, falling back to chunked processing`);
            this._pendingWorkerRequests.delete(requestId);

            // Terminate and recreate worker if it's stuck
            if (this._worker) {
              this._worker.terminate();
              this._worker = null;
              this._pendingWorkerRequests.clear();
            }

            // Fallback to chunked processing
            this.requestMoveWithChunking(request).then(safeResolve).catch(safeReject);
          }
        }, maxTimeout);

      } catch (error) {
        console.error('Failed to send message to AI Worker:', error);
        this._pendingWorkerRequests.delete(requestId);
        // Fallback to chunked processing
        this.requestMoveWithChunking(request).then(safeResolve).catch(safeReject);
      }
    });
  }

  /**
   * Request move on main thread (for simple AI)
   */
  private async requestMoveOnMainThread(request: AIMoveRequest): Promise<AIMoveResult> {
    const strategy = this.getStrategy(request.difficulty);
    const timeLimit = request.timeLimit || 1000;

    // Request move from strategy
    const result = await strategy.selectMove(
      request.gameState,
      request.player,
      timeLimit,
    );

    // Log performance if enabled
    if (this._options.enablePerformanceLogging) {
      this.logPerformance(request.difficulty, strategy.getPerformanceMetrics());
    }

    return result;
  }

  /**
   * Handle worker message response
   */
  private handleWorkerMessage(
    event: MessageEvent,
    resolve?: (result: AIMoveResult) => void,
    reject?: (error: Error) => void,
  ): void {
    const { type, payload } = event.data as {
      type: 'MOVE_RESULT' | 'ERROR';
      payload: {
        id: string;
        result?: AIMoveResult;
        error?: string;
      };
    };

    const pending = payload?.id ? this._pendingWorkerRequests.get(payload.id) : undefined;

    if (type === 'MOVE_RESULT') {
      const result = payload.result as AIMoveResult;

      if (pending) {
        this._pendingWorkerRequests.delete(payload.id);
        pending.resolve(result);
        return;
      }

      if (resolve) {
        resolve(result);
      }
    } else if (type === 'ERROR') {
      const errorMessage = payload.error ?? 'Unknown AI error';

      if (pending) {
        this._pendingWorkerRequests.delete(payload.id);
        pending.reject(new Error(errorMessage));
        return;
      }

      if (reject) {
        reject(new Error(errorMessage));
      }
    }
  }

  /**
   * Request move with chunked processing (fallback for Web Worker issues)
   */
  private async requestMoveWithChunking(request: AIMoveRequest): Promise<AIMoveResult> {
    console.log('🔄 Using chunked processing for AI calculation');

    // Use a simpler/faster AI for chunked processing to reduce blocking
    let fallbackDifficulty = request.difficulty;

    // Reduce complexity for main thread processing
    switch (request.difficulty) {
      case AIDifficulty.MINIMAX_HARD:
      case AIDifficulty.MASTER:
        fallbackDifficulty = AIDifficulty.MINIMAX_EASY; // Use easy instead of hard/master
        break;
      case AIDifficulty.ADVANCED:
      case AIDifficulty.MINIMAX:
        fallbackDifficulty = AIDifficulty.GREEDY; // Use greedy for faster processing
        break;
      case AIDifficulty.BLITZ:
        fallbackDifficulty = AIDifficulty.GREEDY; // Use greedy for blitz mode
        break;
      default:
        // Keep original difficulty for easier ones
        break;
    }

    const strategy = this.getStrategy(fallbackDifficulty);
    // Aggressive time limit to prevent blocking - max 1 second for main thread
    const timeLimit = Math.min(request.timeLimit || 1000, 1000);

    console.log(`🔄 Chunked processing: ${request.difficulty} → ${fallbackDifficulty} (${timeLimit}ms limit)`);

    // Add strict timeout to prevent hanging
    const startTime = Date.now();
    const maxProcessingTime = 2000; // Absolute maximum 2 seconds

    try {
      // Add yielding to prevent complete blocking
      const result = await Promise.race([
        this.executeWithYielding(async () => {
          return await strategy.selectMove(
            request.gameState,
            request.player,
            timeLimit,
          );
        }),
        new Promise<AIMoveResult>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Chunked processing timeout'));
          }, maxProcessingTime);
        })
      ]);

      const actualTime = Date.now() - startTime;
      console.log(`🔄 Chunked processing completed in ${actualTime}ms`);

      // Log performance if enabled
      if (this._options.enablePerformanceLogging) {
        this.logPerformance(fallbackDifficulty, strategy.getPerformanceMetrics());
      }

      return result;

    } catch (error) {
      console.warn('Chunked processing failed, using emergency fallback:', error);

      // Emergency fallback to pure random move
      const emergencyStrategy = createRandomAI.pure();
      return await emergencyStrategy.selectMove(
        request.gameState,
        request.player,
        100, // Very quick emergency move
      );
    }
  }

  /**
   * Execute function with periodic yielding to prevent UI blocking
   */
  private async executeWithYielding<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // Yield control to browser every 100ms
      const yieldInterval = 100;
      let lastYield = Date.now();

      const originalSetTimeout = global.setTimeout;

      // Override setTimeout to add yielding
      global.setTimeout = ((callback: Function, delay: number) => {
        const now = Date.now();
        if (now - lastYield > yieldInterval) {
          lastYield = now;
          // Yield control to browser
          return originalSetTimeout(() => {
            originalSetTimeout(callback, delay);
          }, 0);
        }
        return originalSetTimeout(callback, delay);
      }) as any;

      fn().then(result => {
        global.setTimeout = originalSetTimeout;
        resolve(result);
      }).catch(error => {
        global.setTimeout = originalSetTimeout;
        reject(error);
      });
    });
  }

  /**
   * Get custom configuration for difficulty
   */
  private getCustomConfig(difficulty: AIDifficulty): any {
    if (difficulty === AIDifficulty.CUSTOM && this._customSettings) {
      return this._customSettings;
    }
    return null;
  }

  /**
   * Stop current AI thinking
   */
  stopThinking(): void {
    if (this._currentThinking) {
      if (this.shouldUseWorker(this._currentThinking.difficulty)) {
        // Terminate worker to stop calculation
        if (this._worker) {
          this._worker.terminate();
          this._worker = null;
          this._pendingWorkerRequests.clear();
        }
      } else {
        // Stop main thread strategy
        const strategy = this.getStrategy(
          this._currentThinking.difficulty,
          this._currentThinking.strategyName,
        );
        strategy.stopThinking();
      }
    }
  }

  /**
   * Get current thinking status
   */
  getThinkingStatus(): AIThinkingStatus | null {
    return this._currentThinking ? { ...this._currentThinking } : null;
  }

  /**
   * Check if AI is currently thinking
   */
  isThinking(): boolean {
    return this._currentThinking !== null;
  }

  /**
   * Get available strategies for a difficulty level
   */
  getAvailableStrategies(difficulty: AIDifficulty): StrategyRegistryEntry[] {
    return this._strategyRegistry.get(difficulty) || [];
  }

  /**
   * Get all supported difficulty levels
   */
  getSupportedDifficulties(): AIDifficulty[] {
    return Array.from(this._strategyRegistry.keys());
  }

  /**
   * Get strategy performance metrics
   */
  getStrategyMetrics(difficulty: AIDifficulty, strategyName?: string): AIPerformanceMetrics {
    const strategy = this.getStrategy(difficulty, strategyName);
    return strategy.getPerformanceMetrics();
  }

  /**
   * Reset strategy metrics
   */
  resetStrategyMetrics(difficulty: AIDifficulty, strategyName?: string): void {
    const strategy = this.getStrategy(difficulty, strategyName);
    strategy.resetMetrics();
  }

  /**
   * Get performance history for a difficulty level
   */
  getPerformanceHistory(difficulty: AIDifficulty): AIPerformanceMetrics[] {
    return this._performanceHistory.get(difficulty) || [];
  }

  /**
   * Log performance metrics
   */
  private logPerformance(difficulty: AIDifficulty, metrics: AIPerformanceMetrics): void {
    if (!this._performanceHistory.has(difficulty)) {
      this._performanceHistory.set(difficulty, []);
    }

    const history = this._performanceHistory.get(difficulty)!;
    history.push({ ...metrics });

    // Keep only last 100 entries per difficulty
    if (history.length > 100) {
      history.shift();
    }
  }

  /**
   * Get aggregate performance statistics
   */
  getAggregateStats(difficulty: AIDifficulty): {
    totalGames: number;
    averageThinkingTime: number;
    averageAccuracy: number;
    timeoutRate: number;
  } {
    const history = this._performanceHistory.get(difficulty) || [];

    if (history.length === 0) {
      return {
        totalGames: 0,
        averageThinkingTime: 0,
        averageAccuracy: 0,
        timeoutRate: 0,
      };
    }

    const totalMoves = history.reduce((sum, h) => sum + h.movesPlayed, 0);
    const totalTime = history.reduce((sum, h) => sum + h.averageThinkingTime * h.movesPlayed, 0);
    const totalTimeouts = history.reduce((sum, h) => sum + h.timeoutCount, 0);
    const totalAccuracy = history.reduce((sum, h) => sum + h.accuracy * h.movesPlayed, 0);

    return {
      totalGames: history.length,
      averageThinkingTime: totalMoves > 0 ? totalTime / totalMoves : 0,
      averageAccuracy: totalMoves > 0 ? totalAccuracy / totalMoves : 0,
      timeoutRate: totalMoves > 0 ? totalTimeouts / totalMoves : 0,
    };
  }

  /**
   * Clear all cached strategies (forces recreation)
   */
  clearCache(): void {
    // Stop any current thinking
    this.stopThinking();

    // Clear cached strategies
    this._strategies.clear();
  }

  /**
   * Set AI manager options
   */
  setOptions(options: Partial<AIManagerOptions>): void {
    this._options = { ...this._options, ...options };
  }

  /**
   * Get current options
   */
  getOptions(): AIManagerOptions {
    return { ...this._options };
  }
}

/**
 * Default AI Strategy Manager instance
 */
export const defaultAIManager = new AIStrategyManager({
  defaultDifficulty: AIDifficulty.GREEDY,
  enablePerformanceLogging: true,
  fallbackOnTimeout: true,
});

/**
 * Factory functions for creating AI managers
 */
export const createAIManager = {
  /**
   * Create manager with default settings
   */
  default(): AIStrategyManager {
    return new AIStrategyManager();
  },

  /**
   * Create manager optimized for fast games
   */
  fast(): AIStrategyManager {
    return new AIStrategyManager({
      defaultDifficulty: AIDifficulty.RANDOM,
      enablePerformanceLogging: false,
      fallbackOnTimeout: true,
    });
  },

  /**
   * Create manager optimized for competitive play
   */
  competitive(): AIStrategyManager {
    return new AIStrategyManager({
      defaultDifficulty: AIDifficulty.GREEDY,
      enablePerformanceLogging: true,
      fallbackOnTimeout: false, // No fallbacks in competitive mode
    });
  },

  /**
   * Create manager with custom options
   */
  custom(options: AIManagerOptions): AIStrategyManager {
    return new AIStrategyManager(options);
  },
};